"use client";

import { useQuery } from "@tanstack/react-query";
import { usePathname, useSearchParams } from "next/navigation";
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAnalytics } from "@/core/analytics";
import { defineAssessmentByAnonymousId, getRetrieveCurrentAssessmentQueryKey, setCurrentAssessmentOriginalLanguage } from "@/core/api/orval/ageless-assessments/ageless-assessments";
import { isTokenExpired } from "@/core/auth/auth";
import { queryClient } from "@/core/caching";
import { isBotUserAgent, isNonInteractiveWebClient } from "@/core/device";
import { logger } from "@/core/logging";
import { type AgelessAssessmentFlowType, AgelessAssessmentUsageContext } from "@orval/model";
import { assessmentSessionStorageService } from "../AssessmentSessionStorageService";
import { resolveAssessmentUsageContext } from "../usageContext";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isUuid(value: string): boolean {
  return UUID_PATTERN.test(value);
}
function resolveDefineUsageContext(searchParams: URLSearchParams, scope: {
  subdomainSlug: string;
  flowType: AgelessAssessmentFlowType;
}): {
  context: AgelessAssessmentUsageContext;
  locationId?: string;
} {
  const {
    usageContext,
    locationId
  } = resolveAssessmentUsageContext(searchParams, scope);
  const locationIdForApi = locationId && isUuid(locationId) ? locationId : undefined;
  if (locationId && locationIdForApi === undefined) {
    logger.warn("assessment_define_invalid_location_id", {
      ...scope,
      usageContext,
      locationId
    });
  }
  return locationIdForApi ? {
    context: usageContext,
    locationId: locationIdForApi
  } : {
    context: usageContext
  };
}
export type AssessmentSessionMode = "ensure" | "consume-only";
export type AssessmentSessionStatus = "resolving" | "authenticated" | "unauthenticated" | "error";
export type AssessmentSessionContextValue = {
  status: AssessmentSessionStatus;
  authorizationHeader: string;
  error: Error | null;
  /**
   * Discard the current assessment token and request a fresh one via
   * `defineAssessmentByAnonymousId`. Use this as the first response to a 401
   * from any assessment-scoped endpoint — it covers transient clock-skew
   * failures without forcing the user back to the start of the flow.
   */
  reauthenticate: () => Promise<void>;
};
type AssessmentSessionProviderProps = {
  subdomainSlug: string;
  flowType: AgelessAssessmentFlowType;
  /**
   * Funnel locale (BCP-47, e.g. "fr-CA") from the route. Forwarded to
   * `defineAssessmentByAnonymousId` so the backend captures the assessment's display language
   * and generates the comprehensive analysis in that language.
   */
  locale: string;
  mode: AssessmentSessionMode;
  startStepSlug: string;
  /**
   * Isolates this surface's stored token and session cache from the standard funnel at the
   * same (subdomain, flowType) — see AssessmentSessionStorageScope.variant. The consult-capture
   * page passes "consult" so the texted token never clobbers or leaks into a funnel session.
   */
  storageScopeVariant?: string;
  children?: ReactNode;
};
export const getAssessmentSessionQueryKey = (subdomainSlug: string, flowType: AgelessAssessmentFlowType, storageScopeVariant?: string) => storageScopeVariant ? ["assessment-session", subdomainSlug, flowType, storageScopeVariant] as const : ["assessment-session", subdomainSlug, flowType] as const;
export const AssessmentSessionContext = createContext<AssessmentSessionContextValue | undefined>(undefined);
export function AssessmentSessionProvider({
  children,
  flowType,
  locale,
  mode,
  startStepSlug,
  storageScopeVariant,
  subdomainSlug
}: AssessmentSessionProviderProps) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [urlToken, setUrlToken] = useState(() => searchParams.get("token"));
  const {
    anonymousId,
    isAnonymousIdLoading,
    trackEvent
  } = useAnalytics();
  const currentStepSlug = pathname.split("/").filter(Boolean).at(-1);
  // Crawlers rendering a start-step page must never auto-define a session:
  // each define mints a real backend assessment aggregate, and bots were
  // creating thousands of phantom anonymous assessments. Consume-only lets a
  // bot use an existing token (it has none) but never creates one.
  const isBotVisitor = useMemo(() => isBotUserAgent(), []);
  const sessionMode: AssessmentSessionMode = !isBotVisitor && (mode === "ensure" || currentStepSlug === startStepSlug) ? "ensure" : "consume-only";
  useEffect(() => {
    if (!urlToken) return;
    const cleaned = new URLSearchParams(searchParams.toString());
    cleaned.delete("token");
    const qs = cleaned.toString();
    window.history.replaceState({}, "", qs ? `${pathname}?${qs}` : pathname);
    setUrlToken(null);
  }, [pathname, searchParams, urlToken]);
  const shouldWaitForAnonymousId = sessionMode === "ensure" && !urlToken && isAnonymousIdLoading;
  // Set when define resolved but returned an empty token for a non-interactive
  // client (edge bot-challenge served a 2xx without the JSON DTO). Guards the
  // consume-only → ensure recovery effect below: that null is terminal for this
  // mount, and without the guard the effect would fire one redundant define per
  // crawler page load. Deliberately NOT a UA check in the effect itself — QA
  // Wolf's headless sessions rely on the recovery refetch when define succeeds.
  const nonInteractiveEmptyTokenRef = useRef(false);
  // At most one define POST in flight per provider: refetch() cancels a running queryFn and
  // starts another, but a POST the cancelled run already sent still mints an aggregate, and
  // every mounted useCurrentAssessment consumer calls reauthenticate() on the same 401.
  const inFlightDefineRef = useRef<ReturnType<typeof defineAssessmentByAnonymousId> | null>(null);
  const {
    data: token,
    error: tokenError,
    isLoading: isTokenLoading,
    isFetching: isTokenFetching,
    refetch
  } = useQuery({
    queryKey: getAssessmentSessionQueryKey(subdomainSlug, flowType, storageScopeVariant),
    enabled: !shouldWaitForAnonymousId,
    queryFn: async (): Promise<string | null> => {
      const scope = {
        subdomainSlug,
        flowType,
        variant: storageScopeVariant
      };

      // An already-expired URL token (e.g. a texted link opened hours later) is treated as
      // absent instead of stored: storing it would evict a possibly-valid stored token and
      // burn a doomed retrieve → 401 → reauth roundtrip before the expired screen shows.
      if (urlToken && !isTokenExpired(urlToken)) {
        assessmentSessionStorageService.setAuthToken(scope, urlToken);
        // A URL token belongs to an assessment defined elsewhere, so any synced-locale record
        // left by an earlier browser-defined session in this scope is stale — clearing it keeps
        // the locale-sync guard from treating this session as browser-defined and overwriting a
        // language that was chosen when the link was minted.
        assessmentSessionStorageService.removeSyncedOriginalLanguageLocale(scope);
        return urlToken;
      }
      const storedToken = assessmentSessionStorageService.getAuthToken(scope);
      if (storedToken) {
        if (isTokenExpired(storedToken)) {
          logger.warn("assessment_retrieval_token_expired", {
            flowType,
            subdomainSlug
          });
          assessmentSessionStorageService.removeAuthToken(scope);
        } else {
          return storedToken;
        }
      }
      logger.warn("assessment_retrieval_token_not_found", {
        flowType,
        subdomainSlug
      });
      if (sessionMode !== "ensure" || !anonymousId) {
        return null;
      }
      const defineUsageContext = resolveDefineUsageContext(searchParams, scope);
      inFlightDefineRef.current ??= defineAssessmentByAnonymousId({
        anonymousId,
        subdomain: subdomainSlug,
        flowType,
        locale,
        ...defineUsageContext
      }).finally(() => {
        inFlightDefineRef.current = null;
      });
      const result = await inFlightDefineRef.current;
      if (!result.token) {
        if (isNonInteractiveWebClient()) {
          nonInteractiveEmptyTokenRef.current = true;
          logger.warn("assessment_session_define_returned_empty_token_non_interactive", {
            flowType,
            subdomainSlug
          });
          return null;
        }
        const error = new Error("defineAssessmentByAnonymousId returned falsy token");
        logger.error("assessment_session_define_returned_empty_token", {
          flowType,
          subdomainSlug
        }, error);
        throw error;
      }
      assessmentSessionStorageService.setAuthToken(scope, result.token);
      // Funnel start, anchored to the mint rather than to a screen: exactly one per assessment,
      // never for bots (they never define), and the same site whichever entry-screen arm the
      // visitor is in — an arm-dependent emit site would make the rate incomparable across arms.
      trackEvent("assessment_experience_started", {
        flowType
      });
      // Record the locale the backend just captured as the assessment's original language so
      // the locale-sync query below only fires when the funnel locale actually diverges.
      assessmentSessionStorageService.setSyncedOriginalLanguageLocale(scope, locale);
      return result.token;
    }
  });

  // When the layout's `mode` transitions from "consume-only" to "ensure" (e.g. a
  // redirect from a non-start path back to the start step on a fresh session),
  // the queryKey does not change, so the `null` cached during the consume-only
  // run does not auto-refetch. Without this, AssessmentGate keeps redirecting
  // because the token never advances. Re-runs at most once per transition: once
  // queryFn returns a token (or throws), the guards below short-circuit.
  useEffect(() => {
    if (sessionMode !== "ensure" || token !== null || isTokenLoading || shouldWaitForAnonymousId || tokenError || nonInteractiveEmptyTokenRef.current) {
      return;
    }
    void refetch();
  }, [sessionMode, token, isTokenLoading, shouldWaitForAnonymousId, tokenError, refetch]);
  const resolvedToken = token ?? null;
  const authorizationHeader = resolvedToken ? `Bearer ${resolvedToken}` : "";

  // Server-state sync: the backend captures the assessment's original language once, from the
  // locale of the page that defined it, and that language drives what language the analysis is
  // generated in. A mid-session language switch is a full navigation that reuses the stored
  // token under a new locale, so the language must be re-captured here before generation.
  // Guarded to sessions this browser defined: a missing synced-locale record means the token
  // arrived via URL (texted/consult links land on a fixed en-US path) and the assessment's
  // language was chosen elsewhere — it must not be clobbered by the landing page's locale.
  const syncedOriginalLanguageLocale = resolvedToken ? assessmentSessionStorageService.getSyncedOriginalLanguageLocale({
    subdomainSlug,
    flowType,
    variant: storageScopeVariant
  }) : null;
  useQuery({
    queryKey: [...getAssessmentSessionQueryKey(subdomainSlug, flowType, storageScopeVariant), "original-language-sync", locale],
    enabled: Boolean(resolvedToken) && syncedOriginalLanguageLocale !== null && syncedOriginalLanguageLocale !== locale,
    staleTime: Infinity,
    queryFn: async () => {
      await setCurrentAssessmentOriginalLanguage({
        locale
      }, {
        headers: {
          Authorization: `Bearer ${resolvedToken}`
        }
      });
      assessmentSessionStorageService.setSyncedOriginalLanguageLocale({
        subdomainSlug,
        flowType,
        variant: storageScopeVariant
      }, locale);
      return locale;
    }
  });
  const inFlightReauthenticateRef = useRef<Promise<void> | null>(null);
  const reauthenticate = useCallback(() => {
    inFlightReauthenticateRef.current ??= (async () => {
      logger.warn("assessment_session_reauthenticating", {
        flowType,
        subdomainSlug
      });
      assessmentSessionStorageService.removeAuthToken({
        subdomainSlug,
        flowType,
        variant: storageScopeVariant
      });
      queryClient.removeQueries({
        queryKey: getRetrieveCurrentAssessmentQueryKey()
      });
      await refetch();
    })().finally(() => {
      inFlightReauthenticateRef.current = null;
    });
    return inFlightReauthenticateRef.current;
  }, [flowType, refetch, storageScopeVariant, subdomainSlug]);
  const sessionValue = useMemo<AssessmentSessionContextValue>(() => {
    function deriveStatus(): AssessmentSessionStatus {
      if (tokenError) return "error";
      // `isLoading` is false while a cached `null` token is being refetched (the
      // consume-only → ensure recovery above), yet a define is in flight — that is
      // still "resolving", not "unauthenticated", or the gate's stall clock would run
      // against a define that is about to succeed.
      if (isTokenLoading || isTokenFetching || shouldWaitForAnonymousId) return "resolving";
      if (resolvedToken) return "authenticated";
      return "unauthenticated";
    }
    return {
      status: deriveStatus(),
      authorizationHeader,
      error: tokenError,
      reauthenticate
    };
  }, [authorizationHeader, isTokenFetching, isTokenLoading, reauthenticate, resolvedToken, shouldWaitForAnonymousId, tokenError]);
  return <AssessmentSessionContext.Provider value={sessionValue}>
      {children}
    </AssessmentSessionContext.Provider>;
}